import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function TemporaryMembersManagement() {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeData, setUpgradeData] = useState({
    member_type: 'associate',
    worship_id: ''
  });
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchTemporaryMembers();
  }, [pageNum]);

  const fetchTemporaryMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const skip = (pageNum - 1) * pageSize;
      const response = await api.get(
        `/api/v1/members/admin/temporary?skip=${skip}&limit=${pageSize}`
      );
      setMembers(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load temporary members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = (member) => {
    setSelectedMember(member);
    setUpgradeData({
      member_type: 'associate',
      worship_id: ''
    });
    setShowUpgradeModal(true);
  };

  const handleUpgradeChange = (e) => {
    const { name, value } = e.target;
    setUpgradeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpgradeMember = async () => {
    if (!upgradeData.worship_id.trim()) {
      setError('Please enter a worship ID');
      return;
    }

    try {
      setUpgradeLoading(true);
      setError('');
      
      const response = await api.post('/api/v1/members/admin/upgrade', {
        member_id: selectedMember.id,
        member_type: upgradeData.member_type,
        worship_id: upgradeData.worship_id
      });

      setSuccess(`${selectedMember.chi_name} has been upgraded successfully!`);
      setShowUpgradeModal(false);
      setSelectedMember(null);
      
      // Refresh the list
      fetchTemporaryMembers();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upgrade member');
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('member.temporaryMembers', 'Temporary Members Management')}
          </h1>
          <p className="text-gray-600">
            {t('member.temporaryMembersDesc', 'Review and upgrade temporary member registrations')}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-700">{success}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 mt-4">Loading temporary members...</p>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM9 20H4v-2a6 6 0 0112 0v2H9z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No temporary members</h3>
            <p className="text-gray-600">There are no temporary members waiting for upgrade.</p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {member.chi_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.mobile || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.create_date_time ? new Date(member.create_date_time).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleUpgradeClick(member)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition duration-200"
                        >
                          Upgrade
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Upgrade Member
            </h2>
            <p className="text-gray-600 mb-6">
              Upgrading: <span className="font-semibold">{selectedMember.chi_name}</span>
            </p>

            {/* Member Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Member Type
              </label>
              <div className="space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="member_type"
                    value="associate"
                    checked={upgradeData.member_type === 'associate'}
                    onChange={handleUpgradeChange}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <span className="ml-3 text-gray-700">
                    Associate Member
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="member_type"
                    value="regular"
                    checked={upgradeData.member_type === 'regular'}
                    onChange={handleUpgradeChange}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <span className="ml-3 text-gray-700">
                    Regular Member
                  </span>
                </label>
              </div>
            </div>

            {/* Worship ID Input */}
            <div className="mb-6">
              <label htmlFor="worship_id" className="block text-sm font-medium text-gray-700 mb-2">
                Worship Number *
              </label>
              <input
                type="text"
                id="worship_id"
                name="worship_id"
                value={upgradeData.worship_id}
                onChange={handleUpgradeChange}
                placeholder="Enter worship number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={upgradeLoading}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setSelectedMember(null);
                  setError('');
                }}
                disabled={upgradeLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition duration-200 disabled:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgradeMember}
                disabled={upgradeLoading || !upgradeData.worship_id.trim()}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition duration-200 flex items-center justify-center"
              >
                {upgradeLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Upgrading...
                  </>
                ) : (
                  'Confirm Upgrade'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
